import { useQuery } from '@tanstack/react-query'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import apiClient from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useCurrency } from '@/contexts/CurrencyContext'
import { formatMoneyFixed } from '@/lib/currency'
import { cn } from '@/lib/utils'
import type { UpliftFigure } from '@/types/marketingProactive'

function Uplift({ fig }: { fig: UpliftFigure }) {
  if (fig.uplift_pct == null) return <span className="text-xs text-muted-foreground">no baseline yet</span>
  const up = fig.uplift_pct >= 0
  return (
    <span className={cn('inline-flex items-center text-xs font-medium', up ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground')}>
      {up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
      {Math.abs(fig.uplift_pct)}%
    </span>
  )
}

/**
 * The post-event report: what the code did, and how the kit's days compare
 * with the same weekdays of the four weeks before. It reports; it doesn't
 * claim the kit caused the change.
 */
export function KitResults({ kitId }: { kitId: string }) {
  const { currencyCode } = useCurrency()
  const money = (n: number) => formatMoneyFixed(n, currencyCode, { digits: 0 })
  const query = useQuery({
    queryKey: ['marketing', 'kit-report', kitId],
    queryFn: () => apiClient.getMarketingKitReport(kitId),
    staleTime: 60_000,
  })
  const r = query.data?.data

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Results</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!r ? (
          <Skeleton className="h-28 w-full" />
        ) : (
          <>
            <p className="text-sm">{r.verdict}</p>
            {r.phase !== 'not_started' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Code used</p>
                  <p className="text-lg font-semibold">{r.redemptions}</p>
                  <p className="text-xs text-muted-foreground">
                    {money(r.code_revenue)} of bills · {r.guests} guest{r.guests === 1 ? '' : 's'}
                    {r.new_guests > 0 ? `, ${r.new_guests} new` : ''}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Messages sent</p>
                  <p className="text-lg font-semibold">{r.messages_sent}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{r.sales_label}</p>
                  <p className="text-lg font-semibold">{money(r.sales.actual)}</p>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Uplift fig={r.sales} /> vs {money(r.sales.baseline)} usual
                  </p>
                </div>
                {r.featured_units && (
                  <div>
                    <p className="text-xs text-muted-foreground">Featured dishes sold</p>
                    <p className="text-lg font-semibold">{Math.round(r.featured_units.actual)}</p>
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Uplift fig={r.featured_units} /> vs {Math.round(r.featured_units.baseline)} usual
                    </p>
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              “Usual” is the average of the same weekdays in the four weeks before the kit started.
              {r.phase === 'running' ? ' Updates as the kit runs.' : ''}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}
